import React, { useEffect, useState } from 'react';
import Link from 'next/link';

import { useDispatch, useSelector } from 'react-redux';
import { profile } from 'lang/en.json';
import { authSelector } from 'store/auth';
import { companySelector } from 'store/company';
import {
    expertSelector,
    fetchExperts,
    handleFindExpert,
    removeExpert,
    resetExpertData,
} from 'store/company/expert';

import { handleShowSidescreen, hideOverlay, showOverlay } from 'store/ux';
import Star from 'components/Svg/Star';
import styles from 'styles/modules/ProfileBio.module.scss';
import expertStyles from 'styles/modules/Experts.module.scss';
import Expert from 'components/Expert';
import Avatar from 'components/Svg/Avatar';
import ActionConfirm from 'components/popUps/ActionConfirm';
import AddContent from './AddContent';
import Typo from 'components/Typo';

const Experts = () => {
    const { isAuthorized, company } = useSelector(authSelector);
    const {
        data: { plan },
    } = useSelector(companySelector);
    const dispatch = useDispatch();
    const { data, expert } = useSelector(expertSelector);
    const [expertToRemove, setExpertToRemove] = useState(false);
    const [expertToRemoveFake, setExpertToRemoveFake] = useState(null);
    const [limit, setLimit] = useState(plan?.type === 'free' ? 2 : 5);

    useEffect(() => {
        if (!data) {
            dispatch(fetchExperts());
        }
        setLimit(plan.type === 'free' ? 2 : 5);
    }, [data]);

    if (!data) {
        return null;
    }

    const expertsList = data
        ? data.slice(0, limit).map((expert, key) => (
              <Expert
                  key={key}
                  onClick={(expert) => {
                      setExpertToRemove(expert);
                      setExpertToRemoveFake(expert);
                      dispatch(showOverlay());
                  }}
                  data={{ item: expert, key }}
                  onElementClick={(item) => {
                      handleEditExpert(item.id);
                  }}
                  isEditable={(tag) => tag}
                  expertToRemoveFake={expertToRemoveFake}
              />
          ))
        : null;

    const removePopUp = (
        <ActionConfirm
            activeTags={data}
            expertToRemove={expertToRemove}
            onRemove={async () => {
                await dispatch(removeExpert(expertToRemove.id));
                setExpertToRemove(false);
                dispatch(hideOverlay());
            }}
            onCancel={() => {
                setExpertToRemove(false);
                setExpertToRemoveFake(null);
                dispatch(hideOverlay());
            }}
        />
    );

    const handleAddExpert = async () => {
        await dispatch(resetExpertData());
        dispatch(handleShowSidescreen('add-expert'));
    };

    const handleEditExpert = async (id) => {
        if (!expert || expert.id !== id) {
            await dispatch(handleFindExpert(id));
        }
        dispatch(handleShowSidescreen('edit-expert'));
    };

    const loadingPlaceholder = (
        <>
            <div className={[expertStyles.expert, 'loadingText'].join(' ')}>
                <div className={expertStyles.image}>
                    <Avatar />
                </div>
                <div className={expertStyles.body}>
                    <Typo variant={'caption'}>Loading expert</Typo>
                    <Typo variant={'small'}>Loading expert</Typo>
                </div>
            </div>

            <div className={[expertStyles.expert, 'loadingText'].join(' ')}>
                <div className={expertStyles.image}>
                    <Avatar />
                </div>
                <div className={expertStyles.body}>
                    <Typo variant={'caption'}>Loading expert</Typo>
                    <Typo variant={'small'}>Loading expert</Typo>
                </div>
            </div>
        </>
    );

    const loadMore = plan?.type !== 'free' && limit < data.length && (
        <button
            onClick={() => setLimit(plan.type === 'free' ? 2 : data.length)}
            className={[styles.loadMore]}
        >
            {`+${data.length - limit} more`}
        </button>
    );

    return (
        <>
            {removePopUp}
            <aside className={['mb-l', styles.list].join(' ')}>
                <div className={styles.listExperts}>
                    {data ? (
                        expertsList.length > 0 || isAuthorized ? (
                            <>
                                {expertsList} {loadMore}
                            </>
                        ) : (
                            <Typo className={styles.empty}>
                                This company hasn&apos;t added any experts yet
                            </Typo>
                        )
                    ) : (
                        loadingPlaceholder
                    )}

                    {isAuthorized && plan.type === 'free' && data.length > 1 ? (
                        <div className={styles.premium}>
                            <div className={styles.icon}>
                                <Star />
                            </div>
                            <div className={styles.premiumText}>
                                <Link
                                    href={`/${company}/subscription`}
                                    className={styles.premiumLink}
                                >
                                    Upgrade to Plus
                                </Link>
                                <Typo variant={'small'}>
                                    to add/see more experts.
                                </Typo>
                            </div>
                        </div>
                    ) : (
                        <AddContent
                            data={{
                                direction: 'row',
                                label: profile.header.experts.label,
                                onClick: handleAddExpert,
                            }}
                        />
                    )}
                </div>
            </aside>
        </>
    );
};

export default Experts;
